import { useResultStore } from "@/lib/resultStore";
import { API_BASE_URL } from "@/config";
import type { CalculationResult } from "@/types/calculator";

// The calculator goes through the same relative /api path as the rest of the app;
// a hardcoded 127.0.0.1 only worked when the browser ran on the backend host.
const VLLM_BASE = import.meta.env.VITE_VLLM_URL ?? "http://127.0.0.1:8000";
const CALC_BASE = API_BASE_URL;
const MODEL = import.meta.env.VITE_VLLM_MODEL ?? "Qwen/Qwen2.5-7B-Instruct";
const API_KEY = import.meta.env.VITE_VLLM_API_KEY ?? "mykey";

const SYSTEM_PROMPT = `You are an expert GPU sizing assistant for Lenovo LLM deployments.

Use the calculate_gpu_requirements tool whenever the user asks about sizing, GPU requirements, hardware, or model deployment.

Valid useCase values: chatbot, multimodal, codeassistant, content, documentqa, api, research, custom
Valid modelFamily values: llama3-8b, llama3.1-8b, llama3-70b, qwen2.5-7b, qwen2.5-72b, qwen3-8b, qwen3-32b, qwen3-235b-a22b, gemma-7b, gemma2-9b, gemma2-27b, phi-3-mini-4k, phi-4, mistral-small-24b, mixtral-8x7b, gpt-oss-20b, gpt-oss-120b, deepseek-v3, deepseek-coder-33b, deepseek-moe-16b, internvl3-78b, qwen2.5-vl-72b, qwen3-coder-30b-a3b, llama4-scout, gemma4-31b, gemma4-26b-a4b, deepseek-r1-distill-llama-70b, deepseek-v3.2, custom
Valid gpuType values: nvidia-b200-192gb, nvidia-b300-288gb, nvidia-b100-192gb, nvidia-h200-141gb, nvidia-h200-nvl-141gb, nvidia-h100-nvl-94gb, nvidia-h100-80gb, nvidia-h100-80gb-pcie, nvidia-a100-80gb, nvidia-l40s-48gb, nvidia-l4-24gb, nvidia-rtx-6000-ada-48gb, nvidia-rtx-pro-6000-blackwell-server, nvidia-rtx-pro-6000-blackwell-server-450w, nvidia-rtx-pro-6000-blackwell-workstation, nvidia-rtx-pro-6000-blackwell-maxq, nvidia-rtx-pro-4500-blackwell-workstation, nvidia-rtx-pro-4500-blackwell-server, nvidia-rtx-4090-24gb, nvidia-rtx-3090-24gb
Valid precision values: FP32, FP16, BF16, FP8, INT8, INT4/NF4

Use case defaults:
- chatbot: modelFamily=llama3.1-8b, precision=FP16, concurrentUsers=50, inputTokens=1500, outputTokens=400, requestsPerUserPerMinute=2
- multimodal: modelFamily=internvl3-78b, precision=FP16, concurrentUsers=20, inputTokens=3000, outputTokens=512, requestsPerUserPerMinute=1
- codeassistant: modelFamily=qwen3-coder-30b-a3b, precision=FP16, concurrentUsers=30, inputTokens=8000, outputTokens=1000, requestsPerUserPerMinute=1
- content: modelFamily=qwen3-32b, precision=FP16, concurrentUsers=100, inputTokens=512, outputTokens=1500, requestsPerUserPerMinute=0.5
- documentqa: modelFamily=llama3-70b, precision=FP8, concurrentUsers=10, inputTokens=4096, outputTokens=1024, requestsPerUserPerMinute=0.5
- api: modelFamily=qwen3-8b, precision=FP16, concurrentUsers=200, inputTokens=512, outputTokens=256, requestsPerUserPerMinute=4
- research: modelFamily=deepseek-r1-distill-llama-70b, precision=FP16, concurrentUsers=15, inputTokens=8000, outputTokens=3000, requestsPerUserPerMinute=0.2

IMPORTANT - custom model rule:
If the user mentions a model NOT in the modelFamily list, set modelFamily="custom" and customModelName="org/model-name".
Set params=1, layers=1, hidden_size=1, kv_heads=1, heads=1, is_moe=false — backend will fetch real values.

Global defaults: gpuType=nvidia-rtx-pro-6000-blackwell-server, gpuOverhead=20, enableRAG=false, highAvailability=false.`;

const TOOL_DEFINITION = {
  type: "function",
  function: {
    name: "calculate_gpu_requirements",
    description: "Calculate GPU, CPU, and memory requirements for deploying an LLM.",
    parameters: {
      type: "object",
      properties: {
        useCase:          { type: "string", enum: ["chatbot", "multimodal", "codeassistant", "content", "documentqa", "api", "research", "custom"] },
        gpuType:          { type: "string", enum: ["nvidia-b200-192gb", "nvidia-b300-288gb", "nvidia-b100-192gb", "nvidia-h200-141gb", "nvidia-h200-nvl-141gb", "nvidia-h100-nvl-94gb", "nvidia-h100-80gb", "nvidia-h100-80gb-pcie", "nvidia-a100-80gb", "nvidia-l40s-48gb", "nvidia-l4-24gb", "nvidia-rtx-6000-ada-48gb", "nvidia-rtx-pro-6000-blackwell-server", "nvidia-rtx-pro-6000-blackwell-server-450w", "nvidia-rtx-pro-6000-blackwell-workstation", "nvidia-rtx-pro-6000-blackwell-maxq", "nvidia-rtx-pro-4500-blackwell-workstation", "nvidia-rtx-pro-4500-blackwell-server", "nvidia-rtx-4090-24gb", "nvidia-rtx-3090-24gb"] },
        modelFamily:      { type: "string", enum: ["llama3-8b", "llama3.1-8b", "llama3-70b", "qwen2.5-7b", "qwen2.5-72b", "qwen3-8b", "qwen3-32b", "qwen3-235b-a22b", "gemma-7b", "gemma2-9b", "gemma2-27b", "phi-3-mini-4k", "phi-4", "mistral-small-24b", "mixtral-8x7b", "gpt-oss-20b", "gpt-oss-120b", "deepseek-v3", "deepseek-coder-33b", "deepseek-moe-16b", "internvl3-78b", "qwen2.5-vl-72b", "qwen3-coder-30b-a3b", "llama4-scout", "gemma4-31b", "gemma4-26b-a4b", "deepseek-r1-distill-llama-70b", "deepseek-v3.2", "custom"] },
        precision:        { type: "string", enum: ["FP32", "FP16", "BF16", "FP8", "INT8", "INT4/NF4"] },
        concurrentUsers:  { type: "number" },
        requestsPerUserPerMinute: { type: "number", description: "How often each user sends a request (default 2)" },
        inputTokens:      { type: "number" },
        outputTokens:     { type: "number" },
        gpuOverhead:      { type: "number" },
        enableRAG:        { type: "boolean" },
        highAvailability: { type: "boolean" },
        customModelName:  { type: "string" },
        params:           { type: "number" },
        layers:           { type: "number" },
        hidden_size:      { type: "number" },
        kv_heads:         { type: "number" },
        heads:            { type: "number" },
        is_moe:           { type: "boolean" },
      },
      required: ["useCase", "gpuType", "modelFamily", "precision", "concurrentUsers"],
    },
  },
};

// Message type that covers both regular messages and tool messages
type ToolCall = {
  id: string;
  type?: string;
  function: { name: string; arguments: string };
};

type ChatCompletion = {
  choices?: { message?: { content?: string | null; tool_calls?: ToolCall[] } }[];
};

type ToolInput = Record<string, unknown>;

export type Message = {
  role: "user" | "assistant" | "tool";
  content: string | null;
  tool_call_id?: string;
  tool_calls?: ToolCall[];
};

// Serialize messages correctly for vLLM
// - assistant messages with tool_calls must pass the full object as-is
// - tool result messages must pass role=tool with tool_call_id
// - regular user/assistant messages pass content as string
function serializeMessages(messages: Message[]) {
  return messages.map((m) => {
    // Tool result message
    if (m.role === "tool") {
      return {
        role: "tool",
        tool_call_id: m.tool_call_id,
        content: typeof m.content === "string" ? m.content : JSON.stringify(m.content),
      };
    }
    // Assistant message with tool_calls (content is null, has tool_calls array)
    if (m.role === "assistant" && m.tool_calls) {
      return {
        role: "assistant",
        content: m.content ?? null,
        tool_calls: m.tool_calls,
      };
    }
    // Regular user or assistant message
    return {
      role: m.role,
      content: typeof m.content === "string" ? m.content : JSON.stringify(m.content),
    };
  });
}

async function callVLLM(messages: Message[]) {
  const res = await fetch(`${VLLM_BASE}/v1/chat/completions`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${API_KEY}`,
    },
    body: JSON.stringify({
      model: MODEL,
      messages: [
        { role: "system", content: SYSTEM_PROMPT },
        ...serializeMessages(messages),
      ],
      tools: [TOOL_DEFINITION],
      tool_choice: "auto",
      max_tokens: 1000,
      temperature: 0.1,
    }),
  });
  if (!res.ok) throw new Error(`vLLM error: ${res.status} ${await res.text()}`);
  return res.json();
}

function extractToolCall(data: ChatCompletion) {
  const msg = data.choices?.[0]?.message;
  const toolCall = msg?.tool_calls?.[0];
  if (!toolCall) return null;
  return {
    id: toolCall.id,
    name: toolCall.function.name,
    input: JSON.parse(toolCall.function.arguments) as ToolInput,
    // keep raw message for history
    rawMessage: msg,
  };
}

function extractText(data: ChatCompletion): string {
  return data.choices?.[0]?.message?.content ?? "";
}

async function fetchCustomModelInfo(modelName: string): Promise<ToolInput> {
  try {
    // Sent as a path ("org/name"): underscores in model names must survive.
    const modelPath = modelName.split("/").map(encodeURIComponent).join("/");
    const res = await fetch(`${CALC_BASE}/api/model-info/${modelPath}`);
    if (!res.ok) return {};
    return res.json();
  } catch {
    return {};
  }
}

async function callCalculator(input: ToolInput): Promise<CalculationResult> {
  const payload = {
    useCase: "chatbot", gpuType: "nvidia-rtx-pro-6000-blackwell-server", enableRAG: false,
    highAvailability: false, modelFamily: "llama3-8b", precision: "FP16",
    concurrentUsers: 10, inputTokens: 512,
    outputTokens: 256, gpuOverhead: 20, customModelName: "",
    params: 1, layers: 1, hidden_size: 1, kv_heads: 1, heads: 1, is_moe: false,
    ...input,
  };
  const res = await fetch(`${CALC_BASE}/api/calculate`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(`Calculator error: ${res.status} ${await res.text()}`);
  return res.json();
}

export async function runSizingAgent(
  userMessage: string,
  history: Message[] = []
): Promise<{ reply: string; result: CalculationResult | null; history: Message[] }> {

  const messages: Message[] = [
    ...history,
    { role: "user", content: userMessage },
  ];

  // First vLLM call
  const data1 = await callVLLM(messages);
  const toolCall = extractToolCall(data1);

  // No tool call — plain text reply
  if (!toolCall) {
    const reply = extractText(data1);
    messages.push({ role: "assistant", content: reply });
    return { reply, result: null, history: messages };
  }

  const params = { ...toolCall.input };

  // Custom model: fetch real params from HuggingFace
  if (params.modelFamily === "custom" && typeof params.customModelName === "string" && params.customModelName) {
    const modelInfo = await fetchCustomModelInfo(params.customModelName);
    if (modelInfo.params != null)      params.params      = modelInfo.params;
    if (modelInfo.layers != null)      params.layers      = modelInfo.layers;
    if (modelInfo.hidden_size != null) params.hidden_size = modelInfo.hidden_size;
    if (modelInfo.kv_heads != null)    params.kv_heads    = modelInfo.kv_heads;
    if (modelInfo.heads != null)       params.heads       = modelInfo.heads;
    if (modelInfo.is_moe != null)      params.is_moe      = modelInfo.is_moe;
  }

  // Call calculator
  const calcData = await callCalculator(params);
  useResultStore.getState().setResult(calcData);

  // Add assistant tool call message to history — preserve tool_calls array
  messages.push({
    role: "assistant",
    content: toolCall.rawMessage.content ?? null,
    tool_calls: toolCall.rawMessage.tool_calls,
  });

  // Add tool result message to history
  messages.push({
    role: "tool",
    tool_call_id: toolCall.id,
    content: JSON.stringify(calcData),
  });

  // Second vLLM call — explain results
  const data2 = await callVLLM(messages);
  const reply = extractText(data2);
  messages.push({ role: "assistant", content: reply });

  return { reply, result: calcData, history: messages };
}